import type { EgressNodeTrafficPathShare } from '@nadoutong/contracts';
import { useT } from '../../i18n/index.ts';

/**
 * Per-node exit-path mix. Every segment is `signal` — the Deck palette has
 * no categorical hues, and lamp colours would read as health. Identity is
 * the legend. Same recipe as the SKU pool share bar.
 */
interface Props {
  readonly shares: readonly EgressNodeTrafficPathShare[];
  readonly nodeId: string;
  readonly testId?: string;
}

export function ExitShareBar({ shares, nodeId, testId }: Props): JSX.Element {
  const t = useT();
  const total = shares.reduce((sum, share) => sum + share.requests, 0);
  const id = testId ?? `egress-lines-exit-${nodeId}`;
  if (total === 0 || shares.length === 0) {
    return (
      <p className="m-0 font-mono text-micro text-paper-low" data-testid={id}>
        {t('egress_lines.matrix.exit_empty')}
      </p>
    );
  }
  const percents = shares.map((share) => ({
    pathId: share.path_id,
    percent: Math.round((share.requests / total) * 1000) / 10,
  }));
  return (
    <div className="flex min-w-[8rem] flex-col gap-1" data-testid={id}>
      <div className="flex h-2 gap-px overflow-hidden rounded-full bg-line-soft" aria-hidden="true">
        {percents.map((share) => (
          <div
            key={share.pathId}
            className="h-full flex-none bg-signal first:rounded-l-full last:rounded-r-full"
            style={{ width: `${String(share.percent)}%` }}
            data-testid={`${id}-seg-${share.pathId}`}
          />
        ))}
      </div>
      <ul className="m-0 flex list-none flex-wrap gap-x-2 gap-y-0.5 p-0">
        {percents.map((share) => (
          <li key={share.pathId} className="flex items-baseline gap-1 font-mono text-micro">
            <span className="text-paper-mid">{share.pathId}</span>
            <span className="text-paper">{share.percent}%</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
